import React, { useRef } from "react";
import { Boxes, Cloud, Sparkles, ArrowRight, CalendarClock } from "lucide-react";
import Reveal from "./Reveal";
import OrbitRing from "./OrbitRing";
import Typewriter from "./Typewriter";
import EcgBackline from "./EcgBackline";
import { useLanguage } from "../i18n/LanguageContext";
import { useContent } from "../content/siteContent";
import useDaysUntil from "../hooks/useDaysUntil";

const CHIP_ICONS = [Boxes, Cloud, Sparkles];

export default function Hero() {
  const { t, lang } = useLanguage();
  const { content } = useContent();
  const days = useDaysUntil(content.betaDate);
  const glowRef = useRef(null);

  const onMove = (e) => {
    const el = glowRef.current;
    if (!el) return;
    const r = e.currentTarget.getBoundingClientRect();
    el.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
    el.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMove}
      className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 overflow-hidden"
    >
      {/* interactive background */}
      <div className="aurora" aria-hidden="true">
        <b className="b1" />
        <b className="b2" />
        <b className="b3" />
      </div>
      <EcgBackline />
      <div className="absolute inset-0 bg-grain pointer-events-none" aria-hidden="true" />
      <div ref={glowRef} className="cursor-glow" aria-hidden="true" />

      <div className="relative shell">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-14 items-center">
          {/* Copy */}
          <div className="lg:col-span-7">
            <Reveal className="inline-flex items-center gap-2 pl-1.5 pr-3.5 py-1.5 rounded-full border border-line bg-white/70 backdrop-blur">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue text-white">
                <Sparkles size={11} strokeWidth={2.2} />
              </span>
              <span className="kicker text-ink">{t.hero.eyebrow}</span>
            </Reveal>

            <Reveal
              as="h1"
              delay={1}
              data-testid="hero-headline"
              className="mt-6 font-display text-[36px] sm:text-[52px] lg:text-[60px] font-semibold text-ink leading-[1.04] tracking-tight"
            >
              {t.hero.line1}
              <span className="sr-only"> {t.hero.srCompletion}</span>
              <span className="block text-blue" style={{ minHeight: "1.12em" }}>
                <Typewriter key={lang} words={t.hero.phrases} />
              </span>
            </Reveal>

            <Reveal as="p" delay={2} className="mt-6 text-[17px] sm:text-[18px] text-textsoft max-w-[600px] leading-[1.6]">
              {t.hero.sub}
            </Reveal>

            {days != null && (
              <Reveal delay={3} className="mt-6 inline-flex items-center gap-2.5 pl-2 pr-3.5 py-1.5 rounded-full border border-blue/25 bg-blue-soft/50">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue text-white">
                  <CalendarClock size={13} strokeWidth={2} />
                </span>
                <span className="text-[12.5px] font-semibold text-ink">
                  {days} {t.hero.beta.days}
                </span>
                <span className="text-[11.5px] text-textsoft font-mono">· {t.hero.beta.plan}</span>
              </Reveal>
            )}

            <Reveal delay={3} className="mt-6 flex flex-col sm:flex-row gap-3">
              <a href="#demo" data-testid="hero-primary-cta" className="btn btn-primary group">
                {t.nav.demo}
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 group-hover:translate-x-0.5 transition-transform">
                  <ArrowRight size={13} strokeWidth={2.4} />
                </span>
              </a>
              <a href="#investors" data-testid="hero-secondary-cta" className="btn btn-ghost">
                {t.nav.forInvestors}
                <ArrowRight size={14} strokeWidth={2.2} />
              </a>
            </Reveal>

            <Reveal delay={4} className="mt-10 pt-8 border-t border-line flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              {t.hero.chips.map((label, i) => {
                const Icon = CHIP_ICONS[i] || Boxes;
                return (
                  <div key={label} className="inline-flex items-center gap-2.5">
                    <span className="inline-flex w-7 h-7 items-center justify-center rounded-lg bg-green-soft text-green">
                      <Icon size={14} strokeWidth={1.8} />
                    </span>
                    <span className="text-[13px] font-medium text-ink">{label}</span>
                  </div>
                );
              })}
            </Reveal>
          </div>

          {/* Orbit */}
          <div className="lg:col-span-5">
            <Reveal delay={2} className="relative">
              <OrbitRing />
              <div className="mt-2 flex justify-center">
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-line shadow-sm">
                  <Cloud size={13} strokeWidth={1.9} className="text-green" />
                  <span className="text-[11.5px] font-semibold text-ink tracking-wide">{t.hero.cloudTag}</span>
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
